import { Dialog, Select, SelectItem, TextField } from '@frontend/components/ui';
import { usePostAdminFlowTemplate } from '@frontend/editor/data-access';
import { EditorCtx, UICtx } from '@frontend/editor/external-providers';
import { ModalTypesEnum } from '@frontend/editor/interface';
import { FlowChart, PublishStatus } from '@frontend/sorghum/interface';
import { useTemplateData } from '@frontend/sorghum/utils';
import { styled } from '@mui/system';
import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '../box/box';

const ModalContainerStyled = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

type LanguageType = 'en_us' | 'zh_tw' | 'ja_jp' | 'es_es';

export const SaveTemplateModal: FC = () => {
  const [t] = useTranslation();
  const uiState = useContext(UICtx);
  const { elements } = useContext(EditorCtx);
  const { mutate: saveTemplate } = usePostAdminFlowTemplate();
  const { getTemplateData } = useTemplateData();
  const [language, setLanguage] = useState<string>('en_us');
  const [templateType, setTemplateType] = useState<string>('-1');
  const [description, setDescription] = useState<string>('');

  const handleConfirm = useCallback(() => {
    const chart = {
      name: getTemplateData(parseInt(templateType, 10)).title,
      publishStatus: PublishStatus.UNPUBLISH,
      updatedAt: new Date().toISOString(),
      chart: {
        data: elements,
        blockSerialNumber: elements.length,
      },
    } as FlowChart;
    saveTemplate({
      chartTemplateID: parseInt(templateType, 10),
      language: language as LanguageType,
      chart,
      description,
    });
    uiState.closeModal();
  }, [
    description,
    elements,
    getTemplateData,
    language,
    saveTemplate,
    templateType,
    uiState,
  ]);

  const templateList = useMemo(() => {
    const list = [
      getTemplateData(0),
      getTemplateData(1),
      getTemplateData(2),
      getTemplateData(3),
      getTemplateData(4),
      getTemplateData(5),
      getTemplateData(6),
      getTemplateData(7),
      getTemplateData(8),
      getTemplateData(9),
      getTemplateData(10),
      getTemplateData(11),
      getTemplateData(12),
      getTemplateData(13),
      getTemplateData(14),
      getTemplateData(15),
      getTemplateData(16),
      getTemplateData(17),
    ].map((i) => ({
      value: i.id.toString(),
      label: i.title,
    }));
    return list;
  }, [getTemplateData]);

  return (
    <Dialog
      size="xs"
      title="Save Template"
      open={uiState.modalType === ModalTypesEnum.SAVE_TEMPLATE}
      confirmBtnText="save"
      handleClose={uiState.closeModal}
      handleConfirm={handleConfirm}
    >
      <ModalContainerStyled>
        <Select
          size="small"
          label="Template"
          value={templateType}
          onChange={(e) => setTemplateType(e.target.value)}
        >
          <SelectItem value="-1">{'Default'}</SelectItem>
          {templateList.map((i) => (
            <SelectItem key={i.value} value={i.value}>
              {i.label}
            </SelectItem>
          ))}
        </Select>
        <Select
          size="small"
          label="Language"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
        >
          <SelectItem value="zh_tw">{t('dashboard.chinese')}</SelectItem>
          <SelectItem value="en_us">{t('dashboard.english')}</SelectItem>
          <SelectItem value="ja_jp">{t('dashboard.japanese')}</SelectItem>
          <SelectItem value="es_es">{t('dashboard.spanish')}</SelectItem>
        </Select>
        <TextField
          size="small"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="description"
        />
      </ModalContainerStyled>
    </Dialog>
  );
};

export default SaveTemplateModal;
